import { type SessionOutcome, SessionOutcomeSchema } from '@developerz/domain';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { entityUrl, type SessionDetail } from '../lib/sessionDetail';
import { sessionWorth } from '../lib/sessionWorth';
import type { StreamStatus } from '../lib/sseFrames';
import { PageHeader } from './PageHeader';
import { SessionWorthLine } from './SessionWorthLine';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button, buttonClass } from './ui/Button';

// AuditSession's PageHeader (#980): the `GET /v1/sessions/:id` projection as
// context — title, repo · trigger · model line, outcome pill, entity out-link —
// plus the stream pill and the replay/export actions. Presenter only; an
// undefined detail (still loading / failed read) degrades to the generic header.

// The pill stays a short state word ("Disconnected"); the actionable copy +
// retry live in <ErrorState> on the route so the error isn't announced twice.
const STATUS_LABEL: Record<StreamStatus, string> = {
  connecting: 'audit_session.status_connecting',
  live: 'audit_session.status_live',
  done: 'audit_session.status_done',
  error: 'audit_session.status_disconnected',
};

const STATUS_TONE: Record<StreamStatus, StatusTone> = {
  connecting: 'neutral',
  live: 'live',
  done: 'success',
  error: 'danger',
};

/**
 * Session outcome → pill tone + label key, total over {@link SessionOutcome} so
 * a new member is a compile error here. The label used to be the raw enum
 * (plan 101 A16). A value outside the enum — a pod ahead of this bundle during a
 * roll — keeps a neutral pill with the wire word rather than no pill at all.
 */
const OUTCOME: Readonly<Record<SessionOutcome, { tone: StatusTone; key: string }>> = {
  completed: { tone: 'success', key: 'audit_session.session_outcome_completed' },
  escalated: { tone: 'warning', key: 'audit_session.session_outcome_escalated' },
  failed: { tone: 'danger', key: 'audit_session.session_outcome_failed' },
  // 02-3: a deploy roll handed the job to a fresh session — not a failure.
  handed_back: { tone: 'neutral', key: 'audit_session.session_outcome_handed_back' },
};

/** The pill's tone and words for one wire outcome. */
function outcomePill(outcome: string): { tone: StatusTone; label: string } {
  const parsed = SessionOutcomeSchema.safeParse(outcome);
  if (!parsed.success) return { tone: 'neutral', label: outcome };
  const { tone, key } = OUTCOME[parsed.data];
  return { tone, label: t(key) };
}

// The tokens + their WORTH ride in the header's `below` slot rather than beside
// the outcome pill: they are two facts plus a caveat, not a state word.
export function SessionPageHeader(props: {
  readonly detail: SessionDetail | undefined;
  readonly status: StreamStatus;
  readonly exporting: boolean;
  readonly replayOpen: boolean;
  readonly onExport: () => void;
  readonly onToggleReplay: () => void;
}): JSX.Element {
  // Context line in the AuditNode meta voice — data joined, no prose.
  const context = (): string | undefined => {
    const d = props.detail;
    return d ? `${d.repo_full_name} · ${d.trigger} · ${d.model}` : undefined;
  };

  return (
    <PageHeader
      title={props.detail?.title ?? t('audit.session_title')}
      eyebrow={t('app.live')}
      desc={context() ?? t('audit.session_subtitle')}
      below={<SessionWorthLine worth={sessionWorth(props.detail)} />}
      actions={
        <>
          <Show when={props.detail?.outcome} keyed>
            {(o) => {
              const pill = outcomePill(o);
              return <StatusPill tone={pill.tone}>{pill.label}</StatusPill>;
            }}
          </Show>
          <StatusPill tone={STATUS_TONE[props.status]}>{t(STATUS_LABEL[props.status])}</StatusPill>
          <Show when={entityUrl(props.detail)} keyed>
            {(url) => (
              <a
                class={buttonClass({ variant: 'outline' })}
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                {t('audit_session.entity_link', { number: props.detail?.entity_number ?? '' })}
              </a>
            )}
          </Show>
          <Button
            variant="outline"
            aria-expanded={props.replayOpen}
            onClick={() => props.onToggleReplay()}
          >
            {t('audit.replay')}
          </Button>
          <Button variant="outline" onClick={() => props.onExport()} disabled={props.exporting}>
            {t(props.exporting ? 'audit_session.exporting' : 'audit_session.export')}
          </Button>
        </>
      }
    />
  );
}
